<script setup>
import {computed, getCurrentInstance, ref, watch} from 'vue'
import helpers from '@/lib/helpers'
import NotificationConditionPicker from './notification-condition-picker.vue'

const {proxy} = getCurrentInstance()

const props = defineProps({
  rule: {type: Object, required: true},
  basePath: {type: String, required: true},
  channels: {type: Array, default: () => ['inapp', 'push']},
  operators: {type: Array, default: () => []},
})
const emit = defineEmits(['cancel', 'saved', 'deleted', 'activity'])

const CHANNEL_LABEL = {inapp: 'In-app', push: 'Mobile push', email: 'Email'}

const trigger = computed(() => props.rule.trigger || {})

const name = ref(props.rule.name || '')
const enabled = ref(props.rule.enabled !== false)
const selChannels = ref([...(props.rule.channels && props.rule.channels.length ? props.rule.channels : props.channels)])

// auto-delete after N deliveries (fire_limit)
const limitOn = ref(!!props.rule.fire_limit)
const limitN = ref(props.rule.fire_limit || 1)

// focused: null = inherit the event's default, true/false = override
const focused = ref(props.rule.focused === undefined ? null : props.rule.focused)
const focusedOptions = computed(() => {
  const def = trigger.value.focused ? 'focused' : 'not focused'
  return [
    {value: null, text: `Use event default — ${def}`},
    {value: true, text: 'Always focused'},
    {value: false, text: 'Never focused'},
  ]
})

// scope — record types this trigger can be limited to. Explicit `trigger.scopes`
// (e.g. ["Vendor"] for po.create) wins; otherwise fall back to the subject's
// own model.
const about = ref(props.rule.about ? {...props.rule.about} : null)
const gliInput = ref('')
const scopeTypes = computed(() => {
  const s = trigger.value.scopes
  if (Array.isArray(s) && s.length) return s
  const subjRoot = (trigger.value.subject || '').split('.')[0]
  const meta = (trigger.value.vars || {})[subjRoot]
  const short = meta && meta.model ? meta.model.split('\\').pop() : null
  return short ? [short] : []
})
const scopeType = ref('')
watch(scopeTypes, (v) => {
  if (!scopeType.value && v.length) scopeType.value = v[0]
}, {immediate: true})

// condition: delegated to <notification-condition-picker> (checkbox presets +
// optional custom expression, ANDed together) - see config/notification_triggers.php.
const busy = ref(false)
const fieldError = ref('')
const builtCondition = ref(props.rule.condition || '_default')

function applyGli() {
  const t = gliInput.value.trim()
  const m = t.match(/^(\d{2})[-.](\d+)$/)
  if (!m) { helpers.toast('Enter a GLI like 13-01234', 'error'); return }
  const short = (about.value && about.value.type) || scopeType.value || scopeTypes.value[0] || null
  if (!short) { helpers.toast('This event can’t be limited to a record', 'error'); return }
  about.value = {type: short, id: parseInt(m[2], 10), gli: t}
  gliInput.value = ''
}
// type-only scope, no specific record yet - e.g. "any Order" rather than "this Order"
function limitToType() {
  const short = scopeType.value || scopeTypes.value[0] || null
  if (!short) return
  about.value = {type: short, id: null, gli: null}
}
function clearScope() {
  about.value = null
}

async function save() {
  if (busy.value) return
  busy.value = true
  fieldError.value = ''
  const data = {
    action: props.rule.action,
    name: name.value || null,
    condition: builtCondition.value,
    channels: selChannels.value,
    enabled: enabled.value,
    fire_limit: limitOn.value ? Math.max(1, parseInt(limitN.value, 10) || 1) : null,
    focused: focused.value,
  }
  if (about.value && about.value.type) {
    data.about_type = about.value.type
    data.about_id = about.value.id || null
  } else {
    data.about_type = ''
  }

  try {
    if (props.rule.id) {
      await helpers.asyncAction(props.rule._links.update_url.href, 'PUT', {noload: 'nrules', data})
      helpers.toast('Notification updated')
    } else {
      await helpers.asyncAction(props.basePath, 'POST', {noload: 'nrules', data})
      helpers.toast('Notification created')
    }
    emit('saved')
  } catch (e) {
    if (e.response && e.response.fields && e.response.fields.condition) {
      fieldError.value = e.response.fields.condition
    } else {
      helpers.alert(e.message || 'Could not save')
    }
  } finally {
    busy.value = false
  }
}

async function remove() {
  if (!props.rule.id) return
  if (!await helpers.confirm('Delete this notification?')) return
  try {
    await helpers.asyncAction(props.rule._links.delete_url.href, 'DELETE', {noload: 'nrules'})
    helpers.toast('Notification deleted')
    emit('deleted')
  } catch (e) {
    helpers.alert(e.message || 'Could not delete')
  }
}

function gliClick() {
  if (about.value && about.value.gli) proxy.global_lookup(about.value.gli, true)
}
</script>

<template>
  <div class="d-flex flex-column h-100">
    <div class="flex-fill overflow-auto p-3">
      <!-- header -->
      <div class="d-flex align-items-start mb-3">
        <div class="flex-fill">
          <div class="text-muted text-uppercase small">{{ trigger.group }}</div>
          <h5 class="mb-0">{{ trigger.label }}</h5>
          <div class="text-muted small" v-if="trigger.description">{{ trigger.description }}</div>
        </div>
        <b-button variant="link" size="sm" class="text-secondary px-0" @click="emit('cancel')">
          <font-awesome-icon :icon="['fas', 'arrow-left']"/> Change
        </b-button>
      </div>

      <modal-row label="Name">
        <b-form-input v-model="name" size="sm" placeholder="Optional label, e.g. “Rush orders”"/>
      </modal-row>

      <!-- condition: checkbox presets (ANDed) + optional custom expression -->
      <modal-row label="When">
        <div class="w-100">
          <notification-condition-picker :trigger="trigger" v-model="builtCondition"/>
          <b-form-invalid-feedback :force-show="!!fieldError">{{ fieldError }}</b-form-invalid-feedback>
        </div>
      </modal-row>

      <!-- scope -->
      <modal-row label="Scope">
        <div class="w-100">
          <template v-if="about && about.id">
            <b-badge variant="light" class="border p-2 cursor-pointer" @click="gliClick">
              <font-awesome-icon :icon="['fas', 'link']"/> Only for {{ about.gli || (about.type + ' ' + about.id) }}
            </b-badge>
            <b-button variant="link" size="sm" class="text-danger" @click="clearScope">Remove</b-button>
          </template>
          <template v-else-if="about && about.type">
            <div class="d-flex align-items-center flex-wrap">
              <b-badge variant="light" class="border p-2">
                <font-awesome-icon :icon="['fas', 'filter']"/> Any {{ about.type }}
              </b-badge>
              <b-button variant="link" size="sm" class="text-danger" @click="clearScope">Remove</b-button>
            </div>
            <div class="d-flex align-items-center flex-wrap mt-1">
              <b-form-input v-model="gliInput" size="sm" :placeholder="`…or narrow to one ${about.type} (GLI)`"
                            style="max-width: 240px" @keyup.enter="applyGli"/>
              <b-button variant="outline-secondary" size="sm" class="ml-1" @click="applyGli">Set</b-button>
            </div>
          </template>
          <template v-else>
            <div class="d-flex align-items-center flex-wrap">
              <span class="small text-muted mr-2">All records</span>
              <b-form-select
                v-if="scopeTypes.length > 1"
                v-model="scopeType"
                size="sm"
                class="mr-1"
                style="max-width: 130px"
                :options="scopeTypes.map(s => ({value: s, text: s}))"/>
              <b-button v-if="scopeTypes.length" variant="outline-secondary" size="sm" class="mr-2" @click="limitToType">
                Limit to {{ scopeType || scopeTypes[0] }}
              </b-button>
              <b-form-input v-model="gliInput" size="sm" placeholder="…or limit to one record (GLI)"
                            style="max-width: 240px" @keyup.enter="applyGli"/>
              <b-button variant="outline-secondary" size="sm" class="ml-1" @click="applyGli">Set</b-button>
            </div>
            <div v-if="scopeTypes.length" class="small text-muted mt-1">
              Can be limited to: {{ scopeTypes.join(', ') }}
            </div>
          </template>
        </div>
      </modal-row>

      <!-- channels -->
      <modal-row label="Deliver">
        <b-form-checkbox-group v-model="selChannels" class="pt-1">
          <b-form-checkbox v-for="c in channels" :key="c" :value="c">{{ CHANNEL_LABEL[c] || c }}</b-form-checkbox>
        </b-form-checkbox-group>
      </modal-row>

      <modal-row label="Limit">
        <div class="d-flex align-items-center pt-1">
          <b-form-checkbox v-model="limitOn" class="mr-2">Auto-delete after</b-form-checkbox>
          <b-form-input
            v-model.number="limitN"
            type="number"
            min="1"
            size="sm"
            style="width: 70px"
            :disabled="!limitOn"/>
          <span class="ml-2 text-muted small">deliveries</span>
        </div>
      </modal-row>

      <modal-row label="Focused">
        <b-form-select v-model="focused" size="sm" style="max-width: 280px" :options="focusedOptions"/>
      </modal-row>

      <modal-row label="Enabled">
        <b-form-checkbox v-model="enabled" switch class="pt-1"/>
      </modal-row>
    </div>

    <div class="p-2 border-top d-flex align-items-center">
      <b-button v-if="rule.id" variant="link" size="sm" class="text-secondary" @click="emit('activity', rule)">
        <font-awesome-icon :icon="['fas', 'chart-line']"/> Activity
      </b-button>
      <b-button v-if="rule.id" variant="link" size="sm" class="text-danger" @click="remove">Delete</b-button>
      <div class="ml-auto">
        <b-button variant="outline-secondary" size="sm" class="px-3 mr-2" @click="emit('cancel')">Cancel</b-button>
        <b-button variant="primary" size="sm" class="px-4" :disabled="busy || !selChannels.length" @click="save">
          {{ rule.id ? 'Save' : 'Create' }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.font-monospace {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
</style>
